import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ZayacClientMessage, ZayacPrivateState } from '@shum/shared';
import type { ZayacRoomState } from '../../lib/zayacSocket';
import { useCountdown, useSignedCountdown, formatRemaining, formatSigned } from '../../lib/format';

interface ZayacScreensProps {
  state: ZayacRoomState;
  send: (m: ZayacClientMessage) => boolean;
  privateState: ZayacPrivateState | null;
}

export function ZayacScreens({ send, privateState }: ZayacScreensProps) {
  if (!privateState) {
    return (
      <div className="zy-stage">
        <span className="zy-label">Подготовка…</span>
        <h1>Заяц</h1>
        <p>Сейчас начнётся раунд.</p>
      </div>
    );
  }

  switch (privateState.screen) {
    case 'secret':
      return <SecretScreen privateState={privateState} />;
    case 'zayac':
      return <ZayacRoleScreen privateState={privateState} />;
    case 'your_turn':
      return <YourTurn privateState={privateState} send={send} />;
    case 'listening':
      return <Listening privateState={privateState} send={send} />;
    case 'discussion':
      return <Discussion privateState={privateState} />;
    case 'voting':
      return <Voting privateState={privateState} send={send} />;
    case 'reveal':
      return <Reveal privateState={privateState} />;
    case 'round_end':
      return <RoundEnd privateState={privateState} send={send} />;
    case 'game_over':
      return <ZayacGameOver privateState={privateState} send={send} />;
    default:
      return (
        <div className="zy-stage">
          <span className="zy-label">{labelFor(privateState.phase)}</span>
          <h1>Ждём…</h1>
        </div>
      );
  }
}

function labelFor(phase: ZayacPrivateState['phase']): string {
  const map: Record<string, string> = {
    LOBBY: 'Лобби',
    SECRET_DISTRIBUTION: 'Раздача слов',
    ASSOCIATION: 'Ассоциации',
    DISCUSSION: 'Обсуждение',
    VOTING: 'Голосование',
    REVEAL: 'Раскрытие',
    ROUND_END: 'Конец раунда',
    GAME_OVER: 'Игра окончена',
  };
  return map[phase] ?? phase;
}

function Timer({ endsAt }: { endsAt: string | null }) {
  const remaining = useCountdown(endsAt);
  return <div className={`zy-timer ${remaining <= 5 ? 'warn' : ''}`}>{formatRemaining(remaining)}</div>;
}

/** Plays a repeating short beep while `over` is true and `active` is true. */
function useOvertimeBeep(active: boolean, over: boolean) {
  useEffect(() => {
    if (!active || !over) return;
    playBeep();
    const id = window.setInterval(() => playBeep(), 1500);
    return () => window.clearInterval(id);
  }, [active, over]);
}

function playBeep() {
  const Ctor = window.AudioContext ?? (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return;
  let ctx: AudioContext;
  try {
    ctx = new Ctor();
  } catch {
    return;
  }
  const t = ctx.currentTime;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.value = 880;
  gain.gain.setValueAtTime(0.0001, t);
  gain.gain.exponentialRampToValueAtTime(0.24, t + 0.015);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.16);
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(t);
  osc.stop(t + 0.18);
  window.setTimeout(() => void ctx.close().catch(() => {}), 500);
}

function SecretScreen({ privateState }: { privateState: ZayacPrivateState }) {
  return (
    <div className="zy-stage">
      <span className="zy-label">Тема: {privateState.cardCategory ?? '—'} · раунд {privateState.roundNumber}</span>
      <h1>Запомни выделенное слово</h1>
      <WordOptions privateState={privateState} />
      <p className="zy-muted">Придумайте ассоциацию. Не слишком явную — Заяц не должен догадаться.</p>
      {privateState.endsAt && <Timer endsAt={privateState.endsAt} />}
    </div>
  );
}

function ZayacRoleScreen({ privateState }: { privateState: ZayacPrivateState }) {
  return (
    <div className="zy-stage">
      <span className="zy-label">Тема: {privateState.cardCategory ?? '—'} · раунд {privateState.roundNumber}</span>
      <div className="zy-role">Ты — Заяц</div>
      <p className="zy-muted">Ты не знаешь секретное слово. Слушай ассоциации и притворяйся, что знаешь. Оно — одно из этих:</p>
      <WordOptions privateState={privateState} />
      {privateState.endsAt && <Timer endsAt={privateState.endsAt} />}
    </div>
  );
}

function WordOptions({ privateState }: { privateState: ZayacPrivateState }) {
  if (privateState.cardWords.length === 0) return null;
  const secret = privateState.secretWord;
  return (
    <div className="zy-card-grid">
      {privateState.cardWords.map((w, i) => (
        <div key={`${w}-${i}`} className={`zy-card-word ${secret === w ? 'sel' : ''}`}>{w}</div>
      ))}
    </div>
  );
}

function SpeakerQueue({ privateState }: { privateState: ZayacPrivateState }) {
  const speaker = privateState.speaker;
  if (!speaker) return null;
  return (
    <div className="zy-queue">
      {speaker.speakers.map((s, i) => {
        const cls = i < speaker.index ? 'zy-qp done' : i === speaker.index ? 'zy-qp now' : 'zy-qp';
        return (
          <span key={s.playerId} className={cls}>
            {s.name}
          </span>
        );
      })}
    </div>
  );
}

function YourTurn({ privateState, send }: { privateState: ZayacPrivateState; send: (m: ZayacClientMessage) => boolean }) {
  const speaker = privateState.speaker;
  const remaining = useSignedCountdown(speaker?.turnEndsAt ?? null);
  const over = remaining <= 0;
  const cls = remaining <= 3 ? 'danger' : remaining <= 8 ? 'warn' : 'go';
  useOvertimeBeep(true, over);
  return (
    <div className={`zy-speaker speaking-full ${cls === 'go' ? '' : cls}`} data-you="1">
      <span className="zy-go-kicker">Ваш ход · {speaker ? `${speaker.index + 1}/${speaker.total}` : ''}</span>
      <h1 className="zy-go-title">Скажите ассоциацию</h1>
      <span className="zy-go-remaining">{over ? 'время вышло — говорите, нажмите когда закончите' : 'осталось'}</span>
      <div className="zy-speaker-clock">{formatSigned(remaining)}</div>
      <button className="zy-go-cta" onClick={() => send({ type: 'zayac.finish_association' })}>
        {privateState.isCreator ? 'Следующий игрок →' : 'Я сказал своё слово →'}
      </button>
    </div>
  );
}

function Listening({ privateState, send }: { privateState: ZayacPrivateState; send: (m: ZayacClientMessage) => boolean }) {
  const speaker = privateState.speaker;
  const remaining = useSignedCountdown(speaker?.turnEndsAt ?? null);
  return (
    <div className="zy-stage">
      <span className="zy-label">Говорит · {speaker ? `${speaker.index + 1}/${speaker.total}` : ''}</span>
      <div className="zy-speaker">
        <h2>{speaker?.name ?? '…'}</h2>
        <div className="zy-speaker-clock">{formatSigned(remaining)}</div>
      </div>
      <p>Слушайте внимательно. Кто-то один не знает слова.</p>
      {privateState.isCreator && speaker && (
        <button className="zy-cta" onClick={() => send({ type: 'zayac.advance_association', speakerPlayerId: speaker.playerId })}>
          Следующий игрок →
        </button>
      )}
      <SpeakerQueue privateState={privateState} />
    </div>
  );
}

function Discussion({ privateState }: { privateState: ZayacPrivateState }) {
  return (
    <div className="zy-stage">
      <span className="zy-label">Обсуждение · раунд {privateState.roundNumber}</span>
      <h1>Кто Заяц?</h1>
      <p>Обсудите вслух, чья ассоциация была странной. Скоро голосование.</p>
      {privateState.endsAt && <Timer endsAt={privateState.endsAt} />}
    </div>
  );
}

function Voting({ privateState, send }: { privateState: ZayacPrivateState; send: (m: ZayacClientMessage) => boolean }) {
  const [voted, setVoted] = useState<string | null>(privateState.selectedVoteId ?? null);
  useEffect(() => {
    setVoted(privateState.selectedVoteId ?? null);
  }, [privateState.selectedVoteId, privateState.phase]);

  function vote(id: string | null) {
    setVoted(id);
    send({ type: 'zayac.cast_vote', targetPlayerId: id });
  }

  const totals = privateState.voteTotals ?? [];
  const totalVotes = totals.reduce((a, t) => a + t.votes, 0);
  const discussionMerged = privateState.manualVoting;
  const submitted = privateState.votingSubmittedCount;
  const required = privateState.votingPlayerCount;
  const allVoted = submitted >= required;

  return (
    <div className="zy-stage" style={{ alignItems: 'stretch' }}>
      <div style={{ textAlign: 'center' }}>
        <span className="zy-label">{discussionMerged ? 'Обсуждение и голосование' : 'Голосование'}</span>
        <h1>Кто Заяц?</h1>
        {discussionMerged ? (
          <p className="zy-muted">Обсуждайте вслух и голосуйте. Таймера нет — ведущий завершит этап вручную.</p>
        ) : (
          <p className="zy-muted">Голос можно менять. Таймера нет — этап завершает ведущий.</p>
        )}
        <div className="zy-timer">{submitted}/{required}</div>
      </div>
      <div className="zy-vote">
        <div className="zy-targets">
          {privateState.voteTargets.map((p) => (
            <button key={p.playerId} className={`zy-target ${voted === p.playerId ? 'sel' : ''}`} onClick={() => vote(p.playerId)}>
              <span className="tn">{p.name}</span>
              {voted === p.playerId && <span className="tk">Голос</span>}
            </button>
          ))}
        </div>
        <button className="zy-cta ghost" onClick={() => vote(null)} disabled={voted === null} style={{ fontSize: 18, padding: '12px 24px' }}>
          Воздержаться
        </button>
        <div>
          <span className="zy-label">Результаты · {totalVotes} {plural(totalVotes, 'голос', 'голоса', 'голосов')}</span>
          <div className="zy-totals">
            {totals.length === 0 && <p className="zy-muted">Пока никто не голосовал.</p>}
            {totals.map((t) => {
              const name = privateState.voteTargets.find((p) => p.playerId === t.playerId)?.name ?? '—';
              return (
                <div className="zy-vrow" key={t.playerId}>
                  <span className="vn">{name}</span>
                  <span className="vc">{t.votes}</span>
                </div>
              );
            })}
          </div>
        </div>
        {privateState.isCreator && (
          <button
            className="zy-cta"
            onClick={() => { if (!send({ type: 'zayac.end_voting' })) alert('Нет связи с комнатой. Попробуйте ещё раз.'); }}
          >
            {allVoted ? 'Завершить голосование →' : `Завершить · голоса ${submitted}/${required} →`}
          </button>
        )}
      </div>
    </div>
  );
}

function Reveal({ privateState }: { privateState: ZayacPrivateState }) {
  const r = privateState.reveal;
  if (!r) return null;
  return (
    <div className="zy-reveal">
      <span className="zy-label">Раунд {privateState.roundNumber} · раскрытие</span>
      <div className={`zy-caught ${r.caught ? 'yes' : 'no'}`}>{r.caught ? 'Зайца поймали!' : 'Заяц сбежал'}</div>
      <div>
        <span className="zy-label">Заяц</span>
        <div className="zy-rev-name">{r.zayacName}</div>
      </div>
      <div>
        <span className="zy-label">Секретное слово</span>
        <div className="zy-rev-word">{r.secretWord}</div>
      </div>
      {r.topVotedName && (
        <p className="zy-muted">
          Больше всего голосов {r.tied ? 'набрали несколько игроков' : 'собрал'}: <strong>{r.topVotedName}</strong>.
          {!r.caught && r.tied && ' Заяц не был среди них.'}
        </p>
      )}
      <Scoreboard privateState={privateState} />
      <Timer endsAt={privateState.endsAt} />
    </div>
  );
}

function RoundEnd({ privateState, send }: { privateState: ZayacPrivateState; send: (m: ZayacClientMessage) => boolean }) {
  const r = privateState.reveal;
  return (
    <div className="zy-stage">
      <span className="zy-label">Конец раунда {privateState.roundNumber}</span>
      {r && (
        <>
          <div className={`zy-caught ${r.caught ? 'yes' : 'no'}`}>{r.caught ? '+1 мирным' : '+2 зайцу'}</div>
          <p className="zy-muted">
            Заяц был <strong>{r.zayacName}</strong>. Слово: <strong style={{ color: 'var(--accent)' }}>{r.secretWord}</strong>.
          </p>
        </>
      )}
      <Scoreboard privateState={privateState} />
      <button className="zy-cta" onClick={() => send({ type: 'zayac.ready_for_next' })}>
        Готов к следующему раунду →
      </button>
      <p className="zy-muted">Раунд начнётся, когда все будут готовы.</p>
    </div>
  );
}

function Scoreboard({ privateState }: { privateState: ZayacPrivateState }) {
  const sb = privateState.scoreboard ?? [];
  const topScore = sb.length > 0 ? sb[0].score : 0;
  return (
    <div className="zy-sb">
      {sb.map((p, i) => (
        <div key={p.playerId} className={`zy-sb-row ${p.score === topScore && p.score > 0 ? 'lead' : ''}`}>
          <span className="rn">{(i + 1).toString().padStart(2, '0')}</span>
          <span className="nm">{p.name}</span>
          <span className="sc">{p.score}</span>
        </div>
      ))}
    </div>
  );
}

function ZayacGameOver({ privateState, send }: { privateState: ZayacPrivateState; send: (m: ZayacClientMessage) => boolean }) {
  const sb = privateState.scoreboard ?? [];
  const topScore = sb.length > 0 ? sb[0].score : 0;
  const leaders = sb.filter((p) => p.score === topScore && p.score > 0);
  return (
    <div className="zy-stage">
      <span className="zy-label">Игра окончена</span>
      <h1>{leaders.length > 1 ? 'Ничья!' : leaders.length === 1 ? `Победил ${leaders[0].name}` : 'Игра окончена'}</h1>
      <Scoreboard privateState={privateState} />
      <div className="zy-row">
        {privateState.isCreator ? (
          <>
            <button
              type="button"
              className="zy-cta ghost"
              style={{ fontSize: 18, padding: '12px 24px' }}
              onClick={() => {
                if (!send({ type: 'zayac.start' })) alert('Нет связи с комнатой. Попробуйте ещё раз.');
              }}
            >
              Сыграть снова
            </button>
            <Link to="/play/zayac/create" className="zy-cta" style={{ fontSize: 18, padding: '12px 24px' }}>
              Новая игра →
            </Link>
          </>
        ) : (
          <button type="button" className="zy-cta ghost" style={{ fontSize: 18, padding: '12px 24px' }} disabled>
            Ждём ведущего
          </button>
        )}
      </div>
    </div>
  );
}

function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}
